/** Primitivas de interseccao e projecao. Puras, sem estado. */

import type { Vec2 } from './vec.ts';
import { add, cross, dist, dot, len, normalize, scale, sub, perp } from './vec.ts';
import type { Tol } from './tolerance.ts';
import { STRICT_TOL } from './tolerance.ts';

export interface LineGeom {
  /** Um ponto da reta. */
  readonly p: Vec2;
  /** Direcao unitaria. */
  readonly dir: Vec2;
}

export interface CircleGeom {
  readonly c: Vec2;
  readonly r: number;
}

export const lineThrough = (a: Vec2, b: Vec2): LineGeom => ({ p: a, dir: normalize(sub(b, a)) });

/** Projecao ortogonal de um ponto sobre uma reta. */
export function projectOnLine(p: Vec2, l: LineGeom): Vec2 {
  const t = dot(sub(p, l.p), l.dir);
  return add(l.p, scale(l.dir, t));
}

/** Distancia (nao-assinada) de um ponto a uma reta infinita. */
export function distPointLine(p: Vec2, l: LineGeom): number {
  return Math.abs(cross(sub(p, l.p), l.dir));
}

/** Distancia de um ponto a um segmento (com os extremos). */
export function distPointSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const ab = sub(b, a);
  const L2 = dot(ab, ab);
  if (L2 < 1e-24) return dist(p, a);
  const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / L2));
  return dist(p, add(a, scale(ab, t)));
}

/** Distancia de um ponto a uma semirreta (origem `from`, direcao `from -> through`). */
export function distPointRay(p: Vec2, from: Vec2, through: Vec2): number {
  const d = sub(through, from);
  const L2 = dot(d, d);
  if (L2 < 1e-24) return dist(p, from);
  const t = Math.max(0, dot(sub(p, from), d) / L2);
  return dist(p, add(from, scale(d, t)));
}

/** Interseccao de duas retas infinitas. `null` se paralelas ou coincidentes. */
export function intersectLines(l1: LineGeom, l2: LineGeom, tol: Tol = STRICT_TOL): Vec2 | null {
  const den = cross(l1.dir, l2.dir);
  // `dir` sao unitarios, entao |den| = |sen(angulo)|: magnitude de referencia 1.
  if (Math.abs(den) <= tol.eps || Math.abs(den) < 1e-12) return null;
  const t = cross(sub(l2.p, l1.p), l2.dir) / den;
  return add(l1.p, scale(l1.dir, t));
}

/**
 * Interseccao reta-circulo. Devolve 0, 1 ou 2 pontos, ORDENADOS pelo parametro ao
 * longo de `dir` -- a ordem e deterministica, o que importa porque o DAG guarda
 * `branch: 0 | 1` e precisa reencontrar o mesmo ponto na reavaliacao.
 */
export function intersectLineCircle(l: LineGeom, c: CircleGeom): Vec2[] {
  const f = sub(l.p, c.c);
  const b = dot(f, l.dir);
  const cc = dot(f, f) - c.r * c.r;
  const disc = b * b - cc;
  if (disc < -1e-12) return [];
  if (disc <= 1e-12) return [add(l.p, scale(l.dir, -b))];
  const s = Math.sqrt(disc);
  return [add(l.p, scale(l.dir, -b - s)), add(l.p, scale(l.dir, -b + s))];
}

/**
 * Interseccao circulo-circulo. Devolve 0, 1 ou 2 pontos.
 *
 * A ORDEM E DETERMINISTICA: o primeiro ponto e sempre o do lado `-perp`, o segundo o
 * do lado `+perp`, em relacao ao eixo c1 -> c2. Isso e o que faz `branch` significar
 * sempre a mesma coisa entre uma avaliacao e a seguinte -- inclusive depois de o
 * item ser perturbado na checagem de robustez.
 */
export function intersectCircles(k1: CircleGeom, k2: CircleGeom): Vec2[] {
  const d = dist(k1.c, k2.c);
  if (d < 1e-12) return []; // concentricos
  if (d > k1.r + k2.r + 1e-12) return []; // separados
  if (d < Math.abs(k1.r - k2.r) - 1e-12) return []; // um dentro do outro

  const a = (k1.r * k1.r - k2.r * k2.r + d * d) / (2 * d);
  const h2 = k1.r * k1.r - a * a;
  const axis = scale(sub(k2.c, k1.c), 1 / d);
  const base = add(k1.c, scale(axis, a));

  if (h2 <= 1e-12) return [base];

  const h = Math.sqrt(h2);
  const n = perp(axis);
  return [add(base, scale(n, -h)), add(base, scale(n, h))];
}

/** Parametro do ponto ao longo da reta, medido a partir de `l.p` em unidades de `dir`. */
export function paramOnLine(p: Vec2, l: LineGeom): number {
  return dot(sub(p, l.p), l.dir);
}

/** O ponto esta dentro do poligono? (ray casting; borda conta como dentro) */
export function pointInPolygon(p: Vec2, pts: readonly Vec2[], absTol = 1e-9): boolean {
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i] as Vec2;
    const b = pts[(i + 1) % pts.length] as Vec2;
    if (distPointSegment(p, a, b) <= absTol) return true;
  }
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i] as Vec2;
    const b = pts[j] as Vec2;
    if (a.y > p.y !== b.y > p.y) {
      const x = ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
      if (p.x < x) inside = !inside;
    }
  }
  return inside;
}

export { len };
